import { useEffect, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { toast } from 'sonner'
import { Check, ChevronLeft, Copy } from 'lucide-react'
import { PageHeader } from '~/components/PageHeader'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '~/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs'
import { usePermissionGuard } from '~/lib/current-user'
import { seo } from '~/utils/seo'
import { clientId } from '~/lib/auth'

const MCP_PATH = '/mcp'

export const Route = createFileRoute('/mcp-instructions')({
  head: () => ({
    meta: seo({
      title: 'MCP Instructions',
      description: 'How to connect an MCP client to the First Response MCP server.',
    }),
  }),
  component: McpInstructionsPage,
})

function McpInstructionsPage() {
  const allowed = usePermissionGuard('mcp:read')
  const origin = useOrigin()
  const mcpUrl = `${origin}${MCP_PATH}`
  const keycloakClientId = clientId

  const mcpRemoteConfig = JSON.stringify(
    {
      mcpServers: {
        'rapid-response': {
          command: 'npx',
          args: ['-y', 'mcp-remote', mcpUrl || MCP_PATH, '--header', 'x-api-token:${RAPID_RESPONSE_TOKEN}'],
          env: { RAPID_RESPONSE_TOKEN: 'cbp_...' },
        },
      },
    },
    null,
    2,
  )

  const streamableHttpConfig = JSON.stringify(
    {
      mcpServers: {
        'rapid-response': {
          url: mcpUrl || MCP_PATH,
          transport: 'streamable-http',
          headers: { 'x-api-token': 'cbp_...' },
        },
      },
    },
    null,
    2,
  )

  const mcpRemoteCli = String.raw`claude mcp add --scope user rapid-response \
  -- npx -y mcp-remote ${mcpUrl || MCP_PATH} \
  --header "x-api-token:${'${RAPID_RESPONSE_TOKEN}'}"`

  const streamableHttpCli = String.raw`claude mcp add --scope user --transport http rapid-response \
  ${mcpUrl || MCP_PATH} \
  --header "x-api-token: cbp_..."`

  if (!allowed) {
    return <div className="text-muted-foreground text-sm">Checking permissions...</div>
  }

  return (
    <div className="space-y-6">
      <Link
        to="/settings"
        search={{ tab: 'developer' }}
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ChevronLeft className="size-3.5" />
        Developer Options
      </Link>
      <PageHeader
        title="MCP Instructions"
        description="Connect an MCP client to First Response. Every REST endpoint under /api/v1 is automatically exposed as an MCP tool."
      />
      <Card>
        <CardHeader>
          <CardTitle>Endpoint</CardTitle>
          <CardDescription>
            Streamable HTTP transport, stateless. Sign in with your Keycloak
            account (OAuth) from an OAuth-capable client, or send a Personal
            Access Token from{' '}
            <Link
              to="/settings"
              search={{ tab: 'developer' }}
              className="underline underline-offset-2"
            >
              Developer Options
            </Link>
            .
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <code className="block rounded bg-muted px-2 py-1 text-sm">
            POST {mcpUrl || MCP_PATH}
          </code>
          <p className="text-xs text-muted-foreground">
            Required header (OAuth clients):{' '}
            <code>Authorization: Bearer &lt;Keycloak access token&gt;</code>
          </p>
          <p className="text-xs text-muted-foreground">
            Required header (API-key clients): <code>x-api-token: cbp_...</code>
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Claude Chat / Cowork (OAuth)</CardTitle>
          <CardDescription>
            Add a custom connector and sign in with your own Keycloak account. Every tool
            call runs with your First Response role — nothing more.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <ol className="list-decimal space-y-1 pl-5">
            <li>Settings → Connectors → <strong>Add custom connector</strong>.</li>
            <li>URL: <code>{mcpUrl || MCP_PATH}</code></li>
            <li>Authentication: <strong>Always required</strong>.</li>
            <li>
              OAuth client: <strong>Use your own OAuth client</strong>, client ID{' '}
              <code>{keycloakClientId}</code>, leave the secret blank.
            </li>
            <li>Connect — you are sent to Keycloak to sign in, then back to Claude.</li>
          </ol>
          <p className="text-xs text-muted-foreground">
            First time here? Your account is created with no permissions until an admin grants a role.
          </p>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Streamable HTTP MCP clients</CardTitle>
          <CardDescription>
            For clients that speak Streamable HTTP natively (e.g. Claude Code's{' '}
            <code>.mcp.json</code>).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="config">
            <TabsList>
              <TabsTrigger value="config">Config File</TabsTrigger>
              <TabsTrigger value="cli">Claude Code CLI</TabsTrigger>
            </TabsList>
            <TabsContent value="config" className="pt-2">
              <CodeBlock code={streamableHttpConfig} />
            </TabsContent>
            <TabsContent value="cli" className="pt-2">
              <CodeBlock code={streamableHttpCli} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle>Claude Desktop / stdio MCP clients (via mcp-remote)</CardTitle>
          <CardDescription>
            Use this when your client only speaks stdio.{' '}
            <code>mcp-remote</code> bridges stdio to the remote Streamable HTTP
            transport and injects the auth header.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="config">
            <TabsList>
              <TabsTrigger value="config">Config File</TabsTrigger>
              <TabsTrigger value="cli">Claude Code CLI</TabsTrigger>
            </TabsList>
            <TabsContent value="config" className="pt-2">
              <CodeBlock code={mcpRemoteConfig} />
            </TabsContent>
            <TabsContent value="cli" className="pt-2">
              <CodeBlock code={mcpRemoteCli} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  )
}

function useOrigin(): string {
  const [origin, setOrigin] = useState('')
  useEffect(() => {
    setOrigin(globalThis.location.origin)
  }, [])
  return origin
}

function CodeBlock({ code }: Readonly<{ code: string }>) {
  const [copied, setCopied] = useState(false)

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(code)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
      toast.success('Copied to clipboard')
    } catch {
      toast.error('Failed to copy — select and copy manually')
    }
  }

  return (
    <div className="relative">
      <pre className="overflow-x-auto rounded bg-muted px-3 py-2 pr-10 text-xs">
        <code>{code}</code>
      </pre>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={handleCopy}
        aria-label={copied ? 'Copied' : 'Copy to clipboard'}
        className="absolute right-1.5 top-1.5 size-7 p-0 text-muted-foreground hover:text-foreground"
      >
        {copied ? <Check className="size-3.5" /> : <Copy className="size-3.5" />}
      </Button>
    </div>
  )
}
